import React, { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, Mail, AlertCircle } from "lucide-react";
import { authAPI } from "@/lib/api/auth";
import Version from "@/components/Version";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await authAPI.forgotPassword({ email });
      // Anti-enumeration is enforced by the backend: it always returns 200
      // whether the email exists or not. We only reach here on 2xx, so the
      // "Check your email" success state is the right thing to show — and
      // it does not leak account existence.
      setSubmitted(true);
    } catch {
      // Transport failures / 5xx do not leak account existence, so it is
      // safe (and correct) to surface them to the user. Otherwise we
      // silently lie about an email that will never arrive (issue #596).
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/bondit_collegues_table.jpeg)",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center">
                <img
                  src="/bondit-logo-trans-bg.png"
                  alt="BondIT Logo"
                  className="h-12 w-auto filter drop-shadow-lg mb-2"
                />
                <p className="text-sm text-white/90 drop-shadow text-center">Reset your password</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 container mx-auto px-4 py-12">
          <div className="w-full max-w-md mx-auto">
            <Card className="shadow-2xl backdrop-blur-sm bg-white border-white/20 dark:bg-white dark:border-white/20">
              {submitted ? (
                <>
                  <CardHeader className="text-center pb-6 bg-white dark:bg-white">
                    <div className="flex justify-center mb-4">
                      <div className="rounded-full bg-blue-100 p-3">
                        <Mail className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <CardTitle className="text-2xl text-gray-900 dark:text-gray-900">
                      Check your email
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-600">
                      If an account exists for{" "}
                      <span className="font-medium text-gray-800">{email}</span>, you will receive a
                      password reset link shortly.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="bg-white dark:bg-white">
                    <p className="text-sm text-gray-500 text-center">
                      Didn&apos;t receive an email? Check your spam folder or try again with a
                      different address.
                    </p>
                  </CardContent>
                  <CardFooter className="bg-white dark:bg-white flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setSubmitted(false);
                        setEmail("");
                      }}
                    >
                      Try again
                    </Button>
                    <Button asChild variant="ghost" className="w-full text-gray-600">
                      <Link to="/login">Back to sign in</Link>
                    </Button>
                  </CardFooter>
                </>
              ) : (
                <form onSubmit={handleSubmit}>
                  <CardHeader className="text-center pb-6 bg-white dark:bg-white">
                    <CardTitle className="text-2xl text-gray-900 dark:text-gray-900">
                      Forgot password?
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-600">
                      Enter your email address and we&apos;ll send you a link to reset your
                      password.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 bg-white dark:bg-white">
                    {error ? (
                      <div
                        role="alert"
                        className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                      >
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{error}</span>
                      </div>
                    ) : null}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700 dark:text-gray-700">
                        Email address
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        autoComplete="email"
                        autoFocus
                        className="bg-white text-gray-900 border-gray-300 focus:border-blue-500 dark:bg-white dark:text-gray-900 dark:border-gray-300"
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="bg-white dark:bg-white flex-col gap-3">
                    <Button type="submit" disabled={isLoading} className="w-full" size="lg">
                      {isLoading ? "Sending..." : "Send reset link"}
                    </Button>
                    <Button asChild variant="ghost" className="w-full text-gray-600">
                      <Link to="/login">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to sign in
                      </Link>
                    </Button>
                  </CardFooter>
                </form>
              )}
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white/10 backdrop-blur-sm border-t border-white/20 py-6">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <p className="text-sm text-white/80">
                © {new Date().getFullYear()} BondIT Denmark ApS. All rights reserved. (
                <Version className="inline-block py-1 -my-1 text-white/80" showPrefix={false} />)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
